/**
 * Cloud Sync & Production Database Persistence Client
 *
 * Replaces LocalStorage/IndexedDB-only metadata with secure server-side database
 * persistence, while maintaining transparent offline-first resilience.
 */

import type { Range } from '../core/types.js';

export interface CloudWorkbookMeta {
  id: string;
  name: string;
  activeSheetId: string;
  sheetCount: number;
  rowCount: number;
  createdAt: string;
  updatedAt: string;
  isPublic: boolean;
  hasSnapshot: boolean;
}

export interface CloudSnapshotPayload {
  seq: number;
  bytesB64: string;
  checksum: number;
  savedAt?: string;
}

export interface UserPreferences {
  email: string;
  theme?: string;
  zoomLevel?: number;
  activeWorkbookId?: string;
  selections?: Record<string, Range>;
  productMode?: 'spreadsheet' | 'document' | 'slides';
}

export class CloudSyncService {
  private baseUrl: string;

  constructor(baseUrl = '') {
    this.baseUrl = baseUrl;
  }

  /** Lists all workbooks owned by or shared with the authenticated user. */
  async listWorkbooks(): Promise<CloudWorkbookMeta[]> {
    try {
      const res = await fetch(`${this.baseUrl}/api/workbooks`, {
        method: 'GET',
        headers: { 'Accept': 'application/json' },
        credentials: 'include',
      });
      if (!res.ok) return [];
      const data = await res.json();
      return (data.workbooks || []) as CloudWorkbookMeta[];
    } catch {
      return [];
    }
  }

  /** Creates or registers a workbook in the server database. */
  async createWorkbook(params: {
    id?: string;
    name: string;
    activeSheetId?: string;
    isPublic?: boolean;
  }): Promise<CloudWorkbookMeta | null> {
    try {
      const res = await fetch(`${this.baseUrl}/api/workbooks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(params),
      });
      if (!res.ok) return null;
      const data = await res.json();
      return (data.workbook || null) as CloudWorkbookMeta | null;
    } catch {
      return null;
    }
  }

  /** Loads metadata and the latest binary snapshot for a workbook. */
  async loadWorkbook(id: string): Promise<{
    workbook: CloudWorkbookMeta;
    snapshot: CloudSnapshotPayload | null;
  } | null> {
    try {
      const res = await fetch(`${this.baseUrl}/api/workbooks/detail?id=${encodeURIComponent(id)}`, {
        method: 'GET',
        headers: { 'Accept': 'application/json' },
        credentials: 'include',
      });
      if (!res.ok) return null;
      const data = await res.json();
      return data as { workbook: CloudWorkbookMeta; snapshot: CloudSnapshotPayload | null };
    } catch {
      return null;
    }
  }

  /** Persists a workbook snapshot and updated metadata to the server database. */
  async saveSnapshot(params: {
    id: string;
    name?: string;
    activeSheetId?: string;
    sheetCount?: number;
    rowCount?: number;
    snapshot?: {
      seq: number;
      bytes: Uint8Array;
      checksum: number;
    };
  }): Promise<boolean> {
    try {
      let bytesB64 = '';
      if (params.snapshot?.bytes) {
        // Convert Uint8Array to base64
        let binary = '';
        const bytes = params.snapshot.bytes;
        const len = bytes.byteLength;
        for (let i = 0; i < len; i++) {
          binary += String.fromCharCode(bytes[i]!);
        }
        bytesB64 = btoa(binary);
      }

      const payload = {
        id: params.id,
        name: params.name,
        activeSheetId: params.activeSheetId,
        sheetCount: params.sheetCount,
        rowCount: params.rowCount,
        snapshot: params.snapshot
          ? {
            seq: params.snapshot.seq,
            bytesB64,
            checksum: params.snapshot.checksum,
          }
          : undefined,
      };

      const res = await fetch(`${this.baseUrl}/api/workbooks/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      return res.ok;
    } catch {
      return false;
    }
  }

  /** Soft-deletes a workbook from the server database (moves to Trash). */
  async deleteWorkbook(id: string): Promise<boolean> {
    try {
      const res = await fetch(`${this.baseUrl}/api/workbooks/delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ id }),
      });
      return res.ok;
    } catch {
      return false;
    }
  }

  /** Lists soft-deleted workbooks in the user's Trash. */
  async listTrash(): Promise<Array<{ id: string; name: string; deletedAt: string; sheetCount: number; rowCount: number }>> {
    try {
      const res = await fetch(`${this.baseUrl}/api/workbooks/trash`, {
        method: 'GET',
        headers: { 'Accept': 'application/json' },
        credentials: 'include',
      });
      if (!res.ok) return [];
      const data = await res.json();
      return data.trash || [];
    } catch {
      return [];
    }
  }

  /** Restores a soft-deleted workbook from Trash. */
  async restoreWorkbook(id: string): Promise<boolean> {
    try {
      const res = await fetch(`${this.baseUrl}/api/workbooks/restore`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ id }),
      });
      return res.ok;
    } catch {
      return false;
    }
  }

  /** Permanently deletes a workbook and its associated chunks from disk. */
  async permanentDeleteWorkbook(id: string): Promise<boolean> {
    try {
      const res = await fetch(`${this.baseUrl}/api/workbooks/permanent-delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ id }),
      });
      return res.ok;
    } catch {
      return false;
    }
  }

  /** Retrieves user preferences including active selections across workbooks. */
  async getPreferences(): Promise<UserPreferences | null> {
    try {
      const res = await fetch(`${this.baseUrl}/api/user/preferences`, {
        method: 'GET',
        headers: { 'Accept': 'application/json' },
        credentials: 'include',
      });
      if (!res.ok) return null;
      const data = await res.json();
      return (data.preferences || null) as UserPreferences | null;
    } catch {
      return null;
    }
  }

  /** Saves user view preferences and active selection state. */
  async savePreferences(prefs: Partial<UserPreferences> & { workbookId?: string; selection?: Range }): Promise<boolean> {
    try {
      const res = await fetch(`${this.baseUrl}/api/user/preferences`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(prefs),
      });
      return res.ok;
    } catch {
      return false;
    }
  }

  async getEditorArtifact(workbookId: string, mode: 'document' | 'slides'): Promise<{ title: string; content: string; updatedAt?: string } | null> {
    try { const res = await fetch(`${this.baseUrl}/api/editor-artifacts?workbookId=${encodeURIComponent(workbookId)}&mode=${mode}`, { credentials: 'include' }); if (!res.ok) return null; const data = await res.json(); return data.artifact ?? null; } catch { return null; }
  }

  async saveEditorArtifact(params: { workbookId: string; mode: 'document' | 'slides'; title: string; content: string; baseUpdatedAt?: string | undefined }): Promise<{ ok: boolean; conflict?: boolean; artifact?: { title: string; content: string; updatedAt?: string } }> {
    try { const res = await fetch(`${this.baseUrl}/api/editor-artifacts?workbookId=${encodeURIComponent(params.workbookId)}&mode=${params.mode}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ title: params.title, content: params.content, baseUpdatedAt: params.baseUpdatedAt }) }); const data = await res.json().catch(() => ({})); return { ok: res.ok, conflict: res.status === 409 || data.conflict === true, artifact: data.artifact }; } catch { return { ok: false }; }
  }

  /** Gets permissions for a workbook. */
  async getPermissions(id: string): Promise<{
    userRole: 'owner' | 'editor' | 'viewer';
    ownerEmail: string;
    isPublic: boolean;
    publicRole: 'viewer' | 'editor';
    permissions: { email: string; role: 'editor' | 'viewer' }[];
  } | null> {
    try {
      const res = await fetch(`${this.baseUrl}/api/workbooks/permissions?id=${encodeURIComponent(id)}`, {
        method: 'GET',
        headers: { 'Accept': 'application/json' },
        credentials: 'include',
      });
      if (!res.ok) return null;
      const data = await res.json();
      return data as {
        userRole: 'owner' | 'editor' | 'viewer';
        ownerEmail: string;
        isPublic: boolean;
        publicRole: 'viewer' | 'editor';
        permissions: { email: string; role: 'editor' | 'viewer' }[];
      };
    } catch {
      return null;
    }
  }

  /** Updates workbook permissions and collaborator access. */
  async updatePermissions(params: {
    id: string;
    isPublic?: boolean;
    publicRole?: 'viewer' | 'editor';
    inviteEmail?: string;
    inviteRole?: 'viewer' | 'editor';
    removeEmail?: string;
  }): Promise<boolean> {
    try {
      const res = await fetch(`${this.baseUrl}/api/workbooks/permissions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(params),
      });
      return res.ok;
    } catch {
      return false;
    }
  }

  /** Gets complete workbook metadata and properties. */
  async getMetadata(id: string): Promise<{
    userRole: 'owner' | 'editor' | 'viewer';
    metadata: {
      id: string;
      name: string;
      description: string;
      tags: string[];
      starred: boolean;
      ownerEmail: string;
      lastModifiedBy: string;
      activeSheetId: string;
      sheetCount: number;
      rowCount: number;
      cellCount: number;
      formulaCount: number;
      fileSizeBytes: number;
      createdAt: string;
      updatedAt: string;
      isPublic: boolean;
    };
  } | null> {
    try {
      const res = await fetch(`${this.baseUrl}/api/workbooks/metadata?id=${encodeURIComponent(id)}`, {
        method: 'GET',
        headers: { 'Accept': 'application/json' },
        credentials: 'include',
      });
      if (!res.ok) return null;
      const data = await res.json();
      return data;
    } catch {
      return null;
    }
  }

  /** Updates editable workbook metadata (name, description, tags, starred). */
  async updateMetadata(params: {
    id: string;
    name?: string;
    description?: string;
    tags?: string[];
    starred?: boolean;
    cellCount?: number;
    formulaCount?: number;
  }): Promise<boolean> {
    try {
      const res = await fetch(`${this.baseUrl}/api/workbooks/metadata`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(params),
      });
      return res.ok;
    } catch {
      return false;
    }
  }

  /** Lists saved database connection profiles. */
  async listConnections(): Promise<{
    id: string;
    name: string;
    engine: string;
    host: string;
    port: number;
    dbName: string;
    username: string;
    hasPassword: boolean;
    ssl: boolean;
    createdAt: string;
    updatedAt: string;
  }[]> {
    try {
      const res = await fetch(`${this.baseUrl}/api/db/connections`, {
        method: 'GET',
        headers: { 'Accept': 'application/json' },
        credentials: 'include',
      });
      if (!res.ok) return [];
      const data = await res.json();
      return data.connections || [];
    } catch {
      return [];
    }
  }

  /** Saves or updates a database connection profile. */
  async saveConnection(params: {
    id?: string;
    name: string;
    engine: string;
    host: string;
    port: number;
    dbName?: string;
    username: string;
    password?: string;
    ssl?: boolean;
  }): Promise<boolean> {
    try {
      const res = await fetch(`${this.baseUrl}/api/db/connections`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(params),
      });
      return res.ok;
    } catch {
      return false;
    }
  }

  /** Deletes a saved database connection profile. */
  async deleteConnection(id: string): Promise<boolean> {
    try {
      const res = await fetch(`${this.baseUrl}/api/db/connections/delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ id }),
      });
      return res.ok;
    } catch {
      return false;
    }
  }

  /** Retrieves audit logs for a workbook or system. */
  async getAuditLogs(params?: { workbookId?: string; action?: string; limit?: number }): Promise<{
    id: string;
    workbookId: string;
    userEmail: string;
    action: string;
    details: Record<string, unknown>;
    ipAddress: string;
    timestamp: string;
  }[]> {
    try {
      const query = new URLSearchParams();
      if (params?.workbookId) query.set('workbookId', params.workbookId);
      if (params?.action) query.set('action', params.action);
      if (params?.limit) query.set('limit', String(params.limit));

      const res = await fetch(`${this.baseUrl}/api/audit-logs?${query.toString()}`, {
        method: 'GET',
        headers: { 'Accept': 'application/json' },
        credentials: 'include',
      });
      if (!res.ok) return [];
      const data = await res.json();
      return data.logs || [];
    } catch {
      return [];
    }
  }

  /** Records a client-triggered audit event. */
  async logAuditEvent(event: {
    workbookId?: string;
    action: string;
    details?: Record<string, unknown>;
  }): Promise<boolean> {
    try {
      const res = await fetch(`${this.baseUrl}/api/audit-logs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(event),
      });
      return res.ok;
    } catch {
      return false;
    }
  }

  /** Saves a batch of dirty modified chunks to the cloud database. */
  async saveChunks(params: {
    workbookId: string;
    chunks: Array<{
      sheetId: string;
      chunkId: string;
      bytesB64: string;
      checksum?: number;
      rowStart?: number;
      rowEnd?: number;
      colStart?: number;
      colEnd?: number;
    }>;
  }): Promise<{ ok: boolean; savedCount: number }> {
    try {
      const res = await fetch(`${this.baseUrl}/api/workbooks/chunks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(params),
      });
      if (!res.ok) return { ok: false, savedCount: 0 };
      const data = await res.json();
      return { ok: true, savedCount: data.savedCount || 0 };
    } catch {
      return { ok: false, savedCount: 0 };
    }
  }

  /** Retrieves requested chunk payloads by sheet and chunk IDs. */
  async getChunks(params: {
    workbookId: string;
    sheetId?: string;
    chunkIds?: string[];
  }): Promise<Array<{
    workbookId: string;
    sheetId: string;
    chunkId: string;
    bytesB64: string;
    checksum: number;
    rowStart: number;
    rowEnd: number;
    colStart: number;
    colEnd: number;
    updatedAt: string;
  }>> {
    try {
      const query = new URLSearchParams({ workbookId: params.workbookId });
      if (params.sheetId) query.set('sheetId', params.sheetId);
      if (params.chunkIds && params.chunkIds.length > 0) query.set('chunkIds', params.chunkIds.join(','));

      const res = await fetch(`${this.baseUrl}/api/workbooks/chunks?${query.toString()}`, {
        method: 'GET',
        headers: { 'Accept': 'application/json' },
        credentials: 'include',
      });
      if (!res.ok) return [];
      const data = await res.json();
      return data.chunks || [];
    } catch {
      return [];
    }
  }

  /** Retrieves the chunk manifest summary for a workbook. */
  async getChunkManifest(workbookId: string): Promise<Array<{
    sheetId: string;
    chunkId: string;
    checksum: number;
    rowStart: number;
    rowEnd: number;
    colStart: number;
    colEnd: number;
    byteSize: number;
    updatedAt: string;
  }>> {
    try {
      const res = await fetch(`${this.baseUrl}/api/workbooks/chunks/manifest?workbookId=${encodeURIComponent(workbookId)}`, {
        method: 'GET',
        headers: { 'Accept': 'application/json' },
        credentials: 'include',
      });
      if (!res.ok) return [];
      const data = await res.json();
      return data.manifest || [];
    } catch {
      return [];
    }
  }

  /** Lists import jobs for a workbook or user. */
  async listImportJobs(params?: { workbookId?: string }): Promise<Array<{
    id: string;
    workbookId: string;
    sheetId: string;
    fileName: string;
    fileSize: number;
    sourceType: string;
    status: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';
    progress: number;
    totalRows: number;
    totalCols: number;
    startedAt: string;
    completedAt: string | null;
    error: string | null;
  }>> {
    try {
      const query = new URLSearchParams();
      if (params?.workbookId) query.set('workbookId', params.workbookId);

      const res = await fetch(`${this.baseUrl}/api/import/jobs?${query.toString()}`, {
        method: 'GET',
        headers: { 'Accept': 'application/json' },
        credentials: 'include',
      });
      if (!res.ok) return [];
      const data = await res.json();
      return data.jobs || [];
    } catch {
      return [];
    }
  }

  /** Creates and registers a new async background import job. */
  async createImportJob(params: {
    workbookId: string;
    sheetId?: string;
    fileName: string;
    fileSize?: number;
    sourceType: string;
  }): Promise<{ id: string; status: string } | null> {
    try {
      const res = await fetch(`${this.baseUrl}/api/import/jobs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(params),
      });
      if (!res.ok) return null;
      const data = await res.json();
      return data.job || null;
    } catch {
      return null;
    }
  }

  /** Cancels an in-flight import job. */
  async cancelImportJob(id: string): Promise<boolean> {
    try {
      const res = await fetch(`${this.baseUrl}/api/import/jobs/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ id }),
      });
      return res.ok;
    } catch {
      return false;
    }
  }

  /** Updates an import job's progress. */
  async updateImportJobProgress(id: string, update: {
    progress?: number;
    totalRows?: number;
    totalCols?: number;
    status?: string;
    error?: string;
  }): Promise<boolean> {
    try {
      const res = await fetch(`${this.baseUrl}/api/import/jobs/progress`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ id, ...update }),
      });
      return res.ok;
    } catch {
      return false;
    }
  }

  /** Retrieves historical version snapshots for a workbook. */
  async listVersions(workbookId: string): Promise<Array<{
    id: string;
    workbookId: string;
    version: number;
    seq: number;
    checksum: number;
    byteSize: number;
    rowCount: number;
    savedBy: string;
    savedAt: string;
  }>> {
    try {
      const res = await fetch(`${this.baseUrl}/api/workbooks/versions?workbookId=${encodeURIComponent(workbookId)}`, {
        method: 'GET',
        headers: { 'Accept': 'application/json' },
        credentials: 'include',
      });
      if (!res.ok) return [];
      const data = await res.json();
      return data.versions || [];
    } catch {
      return [];
    }
  }

  /** Restores a specific historical version snapshot. */
  async restoreVersion(versionId: string): Promise<boolean> {
    try {
      const res = await fetch(`${this.baseUrl}/api/workbooks/versions/restore`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ versionId }),
      });
      return res.ok;
    } catch {
      return false;
    }
  }

  /** Lists active multi-device sessions for current user. */
  async listSessions(): Promise<Array<{
    id: string;
    deviceName: string;
    ipAddress: string;
    userAgent: string;
    lastActive: string;
    isCurrent: boolean;
  }>> {
    try {
      const res = await fetch(`${this.baseUrl}/api/user/sessions`, {
        method: 'GET',
        headers: { 'Accept': 'application/json' },
        credentials: 'include',
      });
      if (!res.ok) return [];
      const data = await res.json();
      return data.sessions || [];
    } catch {
      return [];
    }
  }

  /** Revokes a session/device. */
  async revokeSession(sessionId: string): Promise<boolean> {
    try {
      const res = await fetch(`${this.baseUrl}/api/user/sessions/revoke`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ sessionId }),
      });
      return res.ok;
    } catch {
      return false;
    }
  }

  /** Invokes AI Spreadsheet Assistant to generate formulas and explanations. */
  async askSpreadsheetAi(params: {
    prompt: string;
    selection?: { a1?: string; targetCell?: string; startRow?: number; startCol?: number; endRow?: number; endCol?: number };
    workbookId?: string;
  }): Promise<{ ok: boolean; formula?: string; targetCell?: string; explanation?: string; actionType?: string } | null> {
    try {
      const res = await fetch(`${this.baseUrl}/api/ai/assistant`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(params),
      });
      if (!res.ok) return null;
      return await res.json();
    } catch {
      return null;
    }
  }

  /** Invokes AI SQL Assistant to generate dialect-specific optimized queries. */
  async askSqlAssistant(params: {
    prompt: string;
    dialect?: string;
    connectionId?: string;
  }): Promise<{ ok: boolean; sql?: string; explanation?: string; dialect?: string } | null> {
    try {
      const res = await fetch(`${this.baseUrl}/api/ai/sql-assistant`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(params),
      });
      if (!res.ok) return null;
      return await res.json();
    } catch {
      return null;
    }
  }

  /** Tests a Snowflake connection. */
  async testSnowflake(params: { account: string; warehouse?: string; database?: string; username: string; password?: string }): Promise<{ ok: boolean; message?: string; error?: string }> {
    try {
      const res = await fetch(`${this.baseUrl}/api/connectors/snowflake/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(params),
      });
      return await res.json();
    } catch {
      return { ok: false, error: 'Connection failed' };
    }
  }

  /** Queries Snowflake warehouse with optional chunk pagination. */
  async querySnowflake(params: {
    query: string;
    warehouse?: string;
    chunkIndex?: number;
    chunkSize?: number;
  }): Promise<{
    ok: boolean;
    columns?: string[];
    rows?: (string | number | boolean | null)[][];
    totalRows?: number;
    chunkIndex?: number;
    chunkSize?: number;
    totalChunks?: number;
    hasMoreChunks?: boolean;
    nextChunkIndex?: number | null;
    error?: string;
  }> {
    try {
      const res = await fetch(`${this.baseUrl}/api/connectors/snowflake/query`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(params),
      });
      return await res.json();
    } catch {
      return { ok: false, error: 'Query failed' };
    }
  }

  /** Introspects authentic production database schema without fake or hardcoded mock tables. */
  async getDatabaseSchema(params?: { dialect?: string }): Promise<{ ok: boolean; dialect?: string; tablesCount?: number; tables?: Array<{ name: string; description: string; columns: Array<{ name: string; type: string; primaryKey?: boolean; nullable?: boolean; references?: string }> }>; error?: string }> {
    try {
      const res = await fetch(`${this.baseUrl}/api/connectors/schema`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(params || {}),
      });
      return await res.json();
    } catch {
      return { ok: false, error: 'Database schema introspection failed' };
    }
  }

  /** Tests Google BigQuery authentication. */
  async testBigQuery(params: { projectId: string; datasetId?: string }): Promise<{ ok: boolean; message?: string; error?: string }> {
    try {
      const res = await fetch(`${this.baseUrl}/api/connectors/bigquery/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(params),
      });
      return await res.json();
    } catch {
      return { ok: false, error: 'BigQuery test failed' };
    }
  }

  /** Queries Google BigQuery dataset with optional chunk pagination. */
  async queryBigQuery(params: {
    query: string;
    projectId?: string;
    chunkIndex?: number;
    chunkSize?: number;
  }): Promise<{
    ok: boolean;
    columns?: string[];
    rows?: (string | number | boolean | null)[][];
    totalRows?: number;
    chunkIndex?: number;
    chunkSize?: number;
    totalChunks?: number;
    hasMoreChunks?: boolean;
    nextChunkIndex?: number | null;
    bytesProcessed?: string;
    error?: string;
  }> {
    try {
      const res = await fetch(`${this.baseUrl}/api/connectors/bigquery/query`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(params),
      });
      return await res.json();
    } catch {
      return { ok: false, error: 'BigQuery query failed' };
    }
  }

  /** Cancels an in-flight cloud data warehouse query. */
  async cancelCloudQuery(queryId: string): Promise<{ ok: boolean; cancelled?: boolean; message?: string; error?: string }> {
    try {
      const res = await fetch(`${this.baseUrl}/api/connectors/query/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ queryId }),
      });
      return await res.json();
    } catch {
      return { ok: false, error: 'Query cancellation request failed' };
    }
  }

  /** Gets live execution status and progress of an in-flight cloud data warehouse query. */
  async getCloudQueryStatus(queryId: string): Promise<{ ok: boolean; status?: string; phase?: string; percentage?: number; message?: string; error?: string }> {
    try {
      const res = await fetch(`${this.baseUrl}/api/connectors/query/status?queryId=${encodeURIComponent(queryId)}`, {
        method: 'GET',
        headers: { 'Accept': 'application/json' },
        credentials: 'include',
      });
      return await res.json();
    } catch {
      return { ok: false, error: 'Query status request failed' };
    }
  }

  /** Lists objects in an Amazon S3 / R2 bucket. */
  async listS3Objects(params: { bucket?: string; prefix?: string }): Promise<{ ok: boolean; objects?: Array<{ key: string; size: number; format: string; lastModified: string }>; error?: string }> {
    try {
      const res = await fetch(`${this.baseUrl}/api/connectors/s3/list`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(params),
      });
      return await res.json();
    } catch {
      return { ok: false, error: 'S3 listing failed' };
    }
  }

  /** Lists scheduled refresh jobs. */
  async listRefreshSchedules(workbookId?: string): Promise<Array<{ id: string; name: string; cron: string; connectorType: string; lastRunStatus: string; lastRunAt: string }>> {
    try {
      const q = workbookId ? `?workbookId=${encodeURIComponent(workbookId)}` : '';
      const res = await fetch(`${this.baseUrl}/api/jobs/schedules${q}`, {
        method: 'GET',
        headers: { 'Accept': 'application/json' },
        credentials: 'include',
      });
      if (!res.ok) return [];
      const data = await res.json();
      return data.schedules || [];
    } catch {
      return [];
    }
  }

  /** Saves or creates a scheduled refresh job. */
  async saveRefreshSchedule(params: {
    id?: string;
    workbookId: string;
    name: string;
    cron: string;
    connectorType?: string;
    query?: string;
    notifyEmail?: string;
    enabled?: boolean;
  }): Promise<boolean> {
    try {
      const res = await fetch(`${this.baseUrl}/api/jobs/schedules`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(params),
      });
      return res.ok;
    } catch {
      return false;
    }
  }

  /** Triggers a scheduled refresh job immediately. */
  async triggerRefreshSchedule(id: string): Promise<boolean> {
    try {
      const res = await fetch(`${this.baseUrl}/api/jobs/schedules/trigger`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ id }),
      });
      return res.ok;
    } catch {
      return false;
    }
  }

  /** Deletes a scheduled refresh job. */
  async deleteRefreshSchedule(id: string): Promise<boolean> {
    try {
      const res = await fetch(`${this.baseUrl}/api/jobs/schedules/delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ id }),
      });
      return res.ok;
    } catch {
      return false;
    }
  }

  /** Gets active collaborator presences. */
  async getPresence(workbookId: string): Promise<Array<{ userId: string; userEmail: string; userName: string; color: string; sheetId: string; cursor: { row: number; col: number } }>> {
    try {
      const res = await fetch(`${this.baseUrl}/api/collab/presence?workbookId=${encodeURIComponent(workbookId)}`, {
        headers: { 'Accept': 'application/json' },
        credentials: 'include',
      });
      if (!res.ok) return [];
      const data = await res.json();
      return data.presences || [];
    } catch {
      return [];
    }
  }

  /** Sends active user cursor & presence heartbeat. */
  async sendPresence(params: { workbookId: string; sheetId: string; cursor: { row: number; col: number }; color?: string }): Promise<boolean> {
    try {
      const res = await fetch(`${this.baseUrl}/api/collab/presence`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(params),
      });
      return res.ok;
    } catch {
      return false;
    }
  }

  /** Gets comments for a workbook and sheet. */
  async getComments(workbookId: string, sheetId?: string): Promise<Array<{ id: string; row: number; col: number; cellA1: string; authorName: string; content: string; resolved: boolean; replies: Array<{ id: string; content: string }> }>> {
    try {
      const q = sheetId ? `&sheetId=${encodeURIComponent(sheetId)}` : '';
      const res = await fetch(`${this.baseUrl}/api/comments?workbookId=${encodeURIComponent(workbookId)}${q}`, {
        headers: { 'Accept': 'application/json' },
        credentials: 'include',
      });
      if (!res.ok) return [];
      const data = await res.json();
      return data.comments || [];
    } catch {
      return [];
    }
  }

  /** Creates a comment thread or adds a reply. */
  async createComment(params: { workbookId: string; sheetId: string; row: number; col: number; content: string; threadId?: string }): Promise<boolean> {
    try {
      const res = await fetch(`${this.baseUrl}/api/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(params),
      });
      return res.ok;
    } catch {
      return false;
    }
  }

  /** Resolves a comment thread. */
  async resolveComment(id: string): Promise<boolean> {
    try {
      const res = await fetch(`${this.baseUrl}/api/comments/resolve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ id }),
      });
      return res.ok;
    } catch {
      return false;
    }
  }

  /** Gets dashboards for a workbook. */
  async getDashboards(workbookId: string): Promise<Array<{ id: string; name: string; description: string; widgets: Array<{ id: string; title: string; type: string; kpi?: { value: string; trend: string } }> }>> {
    try {
      const res = await fetch(`${this.baseUrl}/api/dashboards?workbookId=${encodeURIComponent(workbookId)}`, {
        headers: { 'Accept': 'application/json' },
        credentials: 'include',
      });
      if (!res.ok) return [];
      const data = await res.json();
      return data.dashboards || [];
    } catch {
      return [];
    }
  }

  /** Saves a dashboard configuration. */
  async saveDashboard(params: { workbookId: string; name: string; widgets?: unknown[] }): Promise<boolean> {
    try {
      const res = await fetch(`${this.baseUrl}/api/dashboards`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(params),
      });
      return res.ok;
    } catch {
      return false;
    }
  }
}

export function isReadOnlySqlQuery(sql: string): boolean {
  if (!sql || typeof sql !== 'string') return false;
  const cleanSql = sql.replace(/\/\*[\s\S]*?\*\/|--.*$/gm, '').trim();
  if (!cleanSql) return false;

  const upper = cleanSql.toUpperCase();
  const firstWord = upper.split(/\s+/)[0] || '';
  if (!['SELECT', 'WITH', 'SHOW', 'DESCRIBE', 'EXPLAIN'].includes(firstWord)) return false;

  const dangerousKeywords = [
    'DROP', 'DELETE', 'UPDATE', 'INSERT', 'ALTER', 'TRUNCATE',
    'GRANT', 'REVOKE', 'EXEC', 'EXECUTE', 'CALL', 'CREATE', 'REPLACE',
    'RENAME', 'SHUTDOWN', 'KILL', 'BACKUP', 'RESTORE', 'ATTACH', 'DETACH',
    'XP_CMDSHELL', 'SP_EXECUTESQL', 'INTO OUTFILE', 'INTO DUMPFILE', 'LOAD DATA',
    'LOAD_FILE', 'BENCHMARK', 'SLEEP', 'PG_SLEEP', 'WAITFOR'
  ];

  for (const kw of dangerousKeywords) {
    if (new RegExp(`\\b${kw.replace(/_/g, '_')}\\b`, 'i').test(upper)) return false;
  }

  // Reject chained statements with semicolons followed by non-read-only queries
  const statements = cleanSql.split(';').map((s) => s.trim()).filter(Boolean);
  if (statements.length > 1) {
    for (const stmt of statements) {
      const stmtUpper = stmt.toUpperCase();
      const first = stmtUpper.split(/\s+/)[0] || '';
      if (!['SELECT', 'WITH', 'SHOW', 'DESCRIBE', 'EXPLAIN'].includes(first)) return false;
      for (const kw of dangerousKeywords) {
        if (new RegExp(`\\b${kw.replace(/_/g, '_')}\\b`, 'i').test(stmtUpper)) return false;
      }
    }
  }

  return true;
}

export const cloudSync = new CloudSyncService();
